/* Neo 카드 Sale Tag(badge) 색상/문구 해석 — NCMS SaleTagNeo 규칙 1:1 정합.
 *
 * 정본: ncms-frontend .../react-library/Product/SaleTagNeo.jsx
 *
 * 핵심: Neo 카드의 badge 색상은 AEM flagJson.color 를 그대로 쓰지 않는다.
 * badge 는 [tagType, tagCategory, tagName] 구조로 해석되며,
 *   1) tagType 우선순위(OMS > Promotion > Product)로 정렬 후 최대 2개만 사용  (SaleTagNeo.jsx:27)
 *   2) 각 badge 의 tagCategory(gradient/solid/solidBlack/line)에 따라
 *      neo 전용 UI 클래스(tag-gr/tag-red-inset/tag-black/tag-red)로 변환       (SaleTagNeo.jsx:17,59,75)
 *   3) tagName 은 AEM tagFlagConfig 매칭 시 표시명이 치환될 수 있으나(SaleTagNeo.jsx:35),
 *      그 치환은 백엔드/상위에서 처리되어 tagName 으로 전달된다고 가정한다.
 *
 * 렌더 클래스 실제 스타일: ncms .../scss/modules/_card-neo.scss:71
 *   tag-gr        그라데이션 배경
 *   tag-red       흰 배경 + 빨강 border/text   (line)
 *   tag-red-inset 연빨강 배경 + 빨강 text       (solid)
 *   tag-black     검정 배경 + 흰 text           (solidBlack)
 *
 * ── SoT / 백엔드 관계 ──────────────────────────────────────────────
 * 이 모듈(`resolveSaleTags`)이 **프로덕션 badge 표시 규칙의 단일 정본(SoT)** 이다.
 * 백엔드는 raw triple 만 내려보낸다 — Coveo raw → `shared/utils/sale_tag_neo.py`
 * `build_sale_tags_neo`(triple 생성) → `mcp/tools/_sale_tags.py` `sale_tags_to_badges`
 * (triple 전달만; 정렬·상위 2개·클래스 미적용). 정렬/상위2/클래스는 전적으로 여기서 한다.
 *
 * 백엔드 `sale_tag_neo.py:resolve_sale_tags_display`/`badge_display_names` 는 본 규칙의
 * 동일 사본이지만 **프로덕션 미사용** — `scripts/tag_parity_probe.py` 와
 * `test/recommend/test_tag_parity_e2e.py` 의 파리티 오라클(테스트 참조) 전용이다.
 * 이 매핑 규칙을 바꾸면 그 백엔드 오라클도 함께 맞춰야 파리티 테스트가 유효하다.
 *
 * OMS badge ``name`` 은 i18n 코드(``sale_tag.oms.*``) — ``PickBadges`` 가 labels 블록으로 해석.
 */

/** OMS SaleTag i18n — ``sale_tag_neo.py`` 와 1:1 (payload triple name = i18n code). */
export const OMS_SALE_TAG_I18N: Record<
  string,
  { labelKey: string; fallback: string }
> = {
  "sale_tag.oms.pre_order": { labelKey: "saleTagOmsPreOrder", fallback: "Pre-Order" },
  "sale_tag.oms.back_order": { labelKey: "saleTagOmsBackOrder", fallback: "Back Order" },
  "sale_tag.oms.limited_offer": {
    labelKey: "saleTagOmsLimitedOffer",
    fallback: "Limited Offer",
  },
};

/** Neo badge 입력: [tagType, tagCategory, tagName]. 레거시 문자열도 허용. */
export type PickBadgeEntry = string | [string, string, string];

/** 화면 표시 문구(tagName) 추출. */
export function pickBadgeLabel(entry: PickBadgeEntry): string {
  return Array.isArray(entry) ? entry[2] : entry;
}

interface NormTag {
  tagType: string;
  tagCategory: string;
  tagName: string;
}

/** tagCategory → 렌더 클래스 (SaleTagNeo.jsx:17 defaultClassMap). */
const _CLASS_MAP: Record<string, string> = {
  gradient: "tag-gr",
  solid: "tag-red-inset",
  solidBlack: "tag-black",
  line: "tag-red",
};

/** tagType 정렬 우선순위 (SaleTagNeo.jsx:27). */
const _TYPE_ORDER: Record<string, number> = {
  OMS: 0,
  Promotion: 1,
  Product: 2,
};

function normTag(entry: PickBadgeEntry): NormTag {
  if (Array.isArray(entry)) {
    return {
      tagType: entry[0] ?? "",
      tagCategory: entry[1] ?? "",
      tagName: entry[2] ?? "",
    };
  }
  // 레거시 문자열 badge: Product/solid 로 취급
  return { tagType: "Product", tagCategory: "solid", tagName: entry };
}

/** SaleTagNeo getTagClass: defaultCategory 와 tagCategory 가 같으면 자기 것, 아니면 defaultCategory. */
function getTagClass(tagCategory: string, defaultCategory: string): string {
  const key = defaultCategory === tagCategory ? tagCategory : defaultCategory;
  return _CLASS_MAP[key] ?? "";
}

/** SaleTagNeo.showTagList — 정렬 → 상위 2개 → 클래스 결정.
 *  조합 결과(요약):
 *   OMS+Promotion → tag-gr, tag-red-inset | OMS+Product → tag-gr, tag-red
 *   Promotion/Product 가 첫 badge 면 각자 tagCategory 기준. */
export function resolveSaleTags(
  badges: PickBadgeEntry[],
): { name: string; tagClass: string }[] {
  const sorted = badges
    .map(normTag)
    .filter((t) => t.tagName)
    .sort(
      (a, b) =>
        (_TYPE_ORDER[a.tagType] ?? 0) - (_TYPE_ORDER[b.tagType] ?? 0),
    );

  const tag1 = sorted[0];
  const tag2 = sorted[1];
  const out: { name: string; tagClass: string }[] = [];

  // 첫 번째 badge (SaleTagNeo.jsx:59): OMS 면 무조건 gradient, 아니면 자기 tagCategory.
  if (tag1?.tagName) {
    let c1 = "";
    switch (tag1.tagType) {
      case "OMS":
        c1 = getTagClass(tag1.tagCategory, "gradient");
        break;
      case "Promotion":
      case "Product":
      default:
        c1 = getTagClass(tag1.tagCategory, tag1.tagCategory);
        break;
    }
    out.push({ name: tag1.tagName, tagClass: c1 });
  }

  // 두 번째 badge (SaleTagNeo.jsx:75): 첫 badge 가 OMS 면 보정, 아니면 자기 tagCategory.
  if (tag2?.tagName) {
    let c2 = "";
    switch (tag1?.tagType) {
      case "OMS":
        if (tag2.tagType === "OMS" || tag2.tagType === "Promotion") {
          c2 = getTagClass(tag2.tagCategory, "solid");
        } else if (tag2.tagType === "Product") {
          c2 = getTagClass(tag2.tagCategory, "line");
        }
        break;
      default:
        c2 = getTagClass(tag2.tagCategory, tag2.tagCategory);
        break;
    }
    out.push({ name: tag2.tagName, tagClass: c2 });
  }

  return out;
}

/** 멀티 심플 carousel(`.pick-list--carousel.is-multiple`) 텍스트 eyebrow(`.product-tag-flag`) 매핑.
 *  ai_top_picks·장바구니 단건 배지와 **동일 출처**(sale_tags triple)를 상단 배지가 아니라 사진 밑
 *  텍스트 flag 로 렌더한다(Figma). 정렬·상위 2개는 `resolveSaleTags`(PickBadges)와 동일 파리티 —
 *  Promotion/OMS 는 accent(highlighted), Product 는 일반. OMS name(i18n code)은 `PickTextFlags`
 *  가 display 언어로 해석하므로 여기서는 원문(코드)을 그대로 넘긴다. */
export function saleTagsToFlags(
  badges: PickBadgeEntry[],
): { text: string; highlighted: boolean }[] {
  return badges
    .map(normTag)
    .filter((t) => t.tagName)
    .sort((a, b) => (_TYPE_ORDER[a.tagType] ?? 0) - (_TYPE_ORDER[b.tagType] ?? 0))
    .slice(0, 2)
    .map((t) => ({ text: t.tagName, highlighted: t.tagType !== "Product" }));
}
